/**
 * Accordion panel displaying the full call transcript with agent and customer script lines.
 *
 * @module components/sim/takeSim/CallTranscript
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState} from "react";
import {Grid, Paper} from "@material-ui/core";
import AgentScript from "./AgentScript.jsx";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import OtherScript from "./OtherScript.jsx";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import {
    audioListStop,
    playAudioList,
    setPlaylist,
    setPlaylistType,
} from "../../../redux/audio/action";
import {audioPlay, audioStop} from "../../../redux/audio/action";
import {currentStatus} from "../../../redux/sims/actions";
import AudioPlaylist from "../../common/AudioPlaylist";

/**
 * Component for rendering the Call Transcript functionality, which manages and displays audio playback, script interactions, and script metadata for user-agent simulations.
 *
 * @param {Object} props - The input properties for the component.
 * @param {Object} props.classes - CSS class styles to be applied to various elements of the component.
 * @param {string} props.simProductType - The product type indicating simulation behavior (e.g., "Voice Only").
 * @param {Object} props.userResult - Data object containing user-related scripts and agent-related scripts for the simulation.
 * @param {Object} props.scriptPlayList - Data object containing the list of scripts and their playback state.
 * @param {Function} props.setPlaylist - Function to set the current playlist with script and playback information.
 * @param {string} props.playListType - Type of playlist mode used to configure playback (e.g., loop, sequential).
 * @param {Function} props.audioListStop - Function to stop the audio playback for the current list when necessary.
 *
 * @returns {React.Component} A React component that provides a user interface for managing and interacting with simulation audio and scripts.
 */
const CallTranscript = ({
    classes,
    simProductType,
    userResult,
    scriptPlayList,
    setPlaylist,
    playListType,
}) => {
    const [scripts, setScripts] = useState([]);
    const [playingScript, setPlayingScript] = useState(null);
    const [audioList, setAudioList] = useState([]);
    const [playMode, setPlayMode] = useState(playListType);

    useEffect(
        () => {
            const [allScripts, userScripts] = scripts;
            let audioScripts = [],
                audios = [];

            if (allScripts) {
                for (let [index, script] of allScripts.entries()) {
                    let isAgent = script.sim_character_id.type === 'agent';
                    let record = {
                        ...script,
                        id: script._id,
                        index: index,
                        is_agent: isAgent,
                        character: {
                            ...script.sim_character_id,
                            id: script.sim_character_id._id,
                        },
                        grade_score: script.grade_score ?? 0,
                    };
                    if (isAgent) {
                        let userScript = userScripts.find((item) => item.sim_script_id === script._id);
                        if (userScript /* && userScript.length */) {
                            //userScript = userScript[0];
                            let newRecord = {
                                ...userScript,
                                ...record,
                            };

                            record = {...newRecord};

                            record.lastFive = userScript.lastFive?.map((item, idx) => {
                                return {
                                    ...item,
                                    id: item.agentId+item.user_response_id,
                                    index: idx,
                                    is_agent: true,
                                    order: item.order,
                                    character: record.character,
                                    script: record.script,
                                    isLastFive: true,
                                };
                            });
                            record.lastFive = record.lastFive.filter((item) => {
                                return !!item;
                            });
                            record.audio_url = userScript.audio_url ?? '';
                            record.myCount = userScript.myCount ?? 0;
                            record.status = userScript.status ?? '';
                            record.grade_score = userScript.grade_score ?? 0;
                            record.has_tests = userScript.has_tests ?? false;
                            record.has_keywords = userScript.has_keywords ?? false;
                            record.missed_tests = userScript.missed_tests ?? [];
                            record.tests_matched = userScript.tests_matched ?? [];
                            record.missed_keywords = userScript.missed_keywords ?? [];
                            record.keywords_matched = userScript.keywords_matched ?? [];
                        }
                    }

                    audioScripts.push({...record});
                    audios.push({
                        id: record.id,
                        audio: record.audio_url ?? record.agent_audio_url ?? record.audio,
                        isAgent: isAgent
                    });
                    if (record.lastFive && record.lastFive.length) {
                        record.lastFive.forEach((item) => {
                            audios.push({
                                id: item.id,
                                audio: item.audio_url ?? item.agent_audio_url ?? item.audio,
                                isAgent: true,
                                isLastFive: true
                            });
                        });
                    }
                }
            }

            setAudioList(audios);
            setPlaylist({scripts: audioScripts, currentIndex: -1, type: playListType});

        },
        [scripts]
    );

    useEffect(
        () => {
            setScripts([userResult?.scripts ?? [], userResult?.agentScripts ?? []]);

            setPlayMode(playListType);
        },
        [playListType]
    )

    useEffect(
        () => {
            setScripts([userResult?.scripts ?? [], userResult?.agentScripts ?? []]);
        },
        [userResult]
    );


    return (
        <Grid container className={classes.girdContainer} item md={simProductType.includes('Voice Only') ? 9 : 5}>
            <Paper className={`${classes.root}`}>
                <h2>Call Transcript</h2>
                <AudioPlaylist audioFiles={audioList}
                               playMode={playMode}
                />

                {scriptPlayList.scripts &&
                    scriptPlayList.scripts.map((singleScript, index) => {
                        if (singleScript.is_agent) {
                            return (
                                <Paper className={classes.agentList} key={index}>
                                    <AgentScript
                                        key={singleScript.id}
                                        index={singleScript.index}
                                        simCharacter={singleScript.character?.type}
                                        script={singleScript}
                                        playingScript={playingScript}
                                        setPlayingScript={setPlayingScript}
                                    />
                                    <div key={singleScript.id+'div'}>
                                        {singleScript?.lastFive?.length ? (
                                        <Accordion className={classes.accordian}>
                                            <AccordionSummary
                                                expandIcon={
                                                    <ExpandMoreIcon className={classes.expand} />
                                                }
                                                aria-controls="panel1a-content"
                                                id="panel1a-header"
                                            >
                                                <p className={classes.heading}>
                                                    Last 5 agent audios
                                                </p>
                                            </AccordionSummary>
                                            {singleScript?.lastFive?.map((item, idx) => {
                                                return (
                                                    <AccordionDetails key={`lastFive${item.user_response_id}`}>
                                                        <AgentScript
                                                            key={`${item.user_response_id}lastFive${idx}`}
                                                            index={idx}
                                                            simCharacter={item?.character?.type}
                                                            script={item}
                                                            noIndex={true}
                                                            playingScript={playingScript}
                                                            setPlayingScript={setPlayingScript}
                                                        />
                                                    </AccordionDetails>
                                                );
                                            })}
                                        </Accordion>
                                        ) : ( <></> )
                                        }
                                    </div>
                                </Paper>
                            );
                        } else {
                            return (
                                <OtherScript
                                    key={singleScript.id}
                                    index={singleScript.index}
                                    simCharacter={singleScript.character?.type}
                                    script={singleScript}
                                    playingScript={playingScript}
                                    setPlayingScript={setPlayingScript}
                                />
                            );
                        }
                    })}
            </Paper>
        </Grid>
    );
}

CallTranscript.propTypes = {
    scriptPlayList: PropTypes.object,
    setPlaylist: PropTypes.func,
    setPlaylistType: PropTypes.func,
    simProductType: PropTypes.string,
    userResult: PropTypes.object,
    userResponse: PropTypes.object,
    callOrAgentPlay: PropTypes.string,
    setCallOrAgentPlay: PropTypes.func,
    audioPlaying: PropTypes.string,
    currentStatus: PropTypes.func,
    setTimePlay: PropTypes.func,
    totalLength: PropTypes.number,
    audioPlay: PropTypes.func,
    audioStop: PropTypes.func,
    classes: PropTypes.object,
    playListType: PropTypes.string,
};

const mapStateToProps = (state) => ({
    scriptPlayList: state.audio,
    userResult: state.sim.userResult,
    userResponse: state.sim.userResponse,
    user: state.login.user,
    audioPlaying: state.sim.audioPlaying,
    simProductType: state.sim.currentSimProductType,
    playListType: state.audio.type,
    playlist: state.audio,
});

const mapDispatchToProps = {
    setPlaylist,
    setPlaylistType,
    playAudioList,
    audioListStop,
    currentStatus,
    audioPlay,
    audioStop,
};

export default connect(mapStateToProps, mapDispatchToProps)(CallTranscript);
